import { Autocomplete, MenuItem } from '@mui/material';
import {
    autocompleteClasses,
    AutocompleteRenderInputParams,
    AutocompleteProps,
} from '@mui/material/Autocomplete';
import React, { ChangeEvent } from 'react';
import styled from '@emotion/styled';
import { __ } from '../../../utils/translation-service';
import { Input } from '../../atoms/input/input';

import allCountries from './countries.json';

interface IBookingCountrySelect {
    onChange: (country: string) => void;
    onBlur?: (country: string) => void;
    marketCountryCode: string;
    value?: string;
    name?: string;
}
export interface ICountryOption {
    [key: string]: string;
}

const StyledAutocomplete = styled(Autocomplete<ICountryOption>)`
    .${autocompleteClasses.inputRoot}, .${autocompleteClasses.inputRoot}:hover {
        padding-right: 0 !important;
    }
    .${autocompleteClasses.endAdornment} {
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
    }
    label + & {
        margin-top: 8px;
    }
`;

export function getFlagEmoji(countryCode: string) {
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
}

const getOptionLabel = (code: string) => {
    const countryName = __(code);
    return {
        label: `${getFlagEmoji(code)} ${countryName}`,
        labelWithoutFlag: countryName,
    };
};

const renderCountryOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: ICountryOption,
) => {
    return (
        <MenuItem {...props} value={option.code} sx={{ fontSize: 14 }}>
            {option.label}
        </MenuItem>
    );
};

const getSelectedOption = (options: ICountryOption[], value = ''): ICountryOption =>
    options.find((c) => c?.code === value) || {};

const countryCodeMap: ICountryOption = {
    c1au: 'au',
    c1uk: 'gb',
};
export const BookingCountrySelect = (props: IBookingCountrySelect) => {
    const countryCode = countryCodeMap[props.marketCountryCode] || props.marketCountryCode;
    const options: ICountryOption[] = React.useMemo(() => {
        return allCountries
            .map(({ code }) => {
                return {
                    ...getOptionLabel(code),
                    code,
                };
            })
            .sort((a, b) =>
                new Intl.Collator(countryCode).compare(a.labelWithoutFlag, b.labelWithoutFlag),
            );
    }, [allCountries]);

    const [selectedCountry, setSelectedCountry] = React.useState(
        getSelectedOption(options, props.value),
    );

    React.useEffect(() => {
        setSelectedCountry(getSelectedOption(options, props.value));
    }, [props.value]);

    const handleBlur = () => {
        props.onBlur?.(selectedCountry?.code);
    };

    // TODO: fix types on autocmplete

    return (
        <StyledAutocomplete
            disablePortal
            freeSolo
            {...props}
            value={selectedCountry}
            onBlur={handleBlur}
            onChange={(e, value) => {
                setSelectedCountry(value);
                props.onChange(value?.code);
            }}
            options={options}
            getOptionLabel={(option) => option?.label || ''}
            renderOption={renderCountryOption}
            renderInput={(params: AutocompleteRenderInputParams) => {
                const { InputLabelProps, InputProps, ...rest } = params;
                return (
                    <div ref={params.InputProps.ref}>
                        <Input {...params.InputProps} {...rest} />
                    </div>
                );
            }}
        />
    );
};
